import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import * as Survey from 'survey-react';
import 'survey-react/survey.css';

const useStyles = makeStyles(theme => ({
    dialogPaper: {
        width: 'calc(100% - 24px)',
        maxWidth: 520,
        margin: 12,
        borderRadius: 16,
        overflowX: 'hidden'
    },
    dialogTitle: {
        fontSize: 15,
        fontWeight: 600,
        padding: '16px 20px'
    },
    dialogContent: {
        padding: '16px 20px 20px',
        overflowX: 'hidden'
    },
    loadingWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 160
    },
    surveyWrapper: {
        width: '100%',
        overflowX: 'hidden',

        '& *': {
            boxSizing: 'border-box'
        },

        '& .sv_main': {
            width: '100%',
            minWidth: '0 !important',
            overflowX: 'hidden',
            backgroundColor: 'transparent'
        },

        '& .sv_container': {
            width: '100%',
            minWidth: '0 !important',
            maxWidth: '100%',
            overflowX: 'hidden'
        },

        '& .sv_body': {
            width: '100%',
            minWidth: '0 !important',
            maxWidth: '100%',
            padding: '0 !important',
            borderTop: '2px solid #ff9800'
        },

        '& .sv_p_root': {
            padding: '16px 0 0 !important'
        },

        '& .sv_q': {
            padding: '0 0 12px !important',
            overflowWrap: 'break-word',
            wordBreak: 'break-word'
        },

        '& .sv_q_title': {
            fontSize: 14,
            lineHeight: 1.45,
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            wordBreak: 'break-word'
        },

        '& .sv_q_checkbox_control_label': {
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            lineHeight: 1.4
        },

        '& .sv_complete_btn': {
            minWidth: 120,
            height: 36,
            fontSize: 12,
            backgroundColor: '#ff9800 !important'
        },

        '& .sv_nav': {
            textAlign: 'right',
            paddingTop: 12,
            borderTop: '1px solid #e0e0e0'
        },

        '& .sv_header': {
            display: 'none !important'
        },

        '& .sv_header__background-image': {
            display: 'none !important'
        },

        '& .sv_header__content': {
            display: 'none !important'
        }
    }
}));

function InstallAttentionForm({
    open,
    title,
    surveyJson,
    loading,
    onComplete
}) {
    const classes = useStyles();
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    const surveyModel = useMemo(() => {
        if (!surveyJson) {
            return null;
        }

        const normalizedSurveyJson = JSON.parse(JSON.stringify(surveyJson));

        // DB'den gelen "headerView": "advanced" mobilde büyük boş header oluşturuyor.
        // Form JSON'una DB tarafında dokunmadan UI'da temizliyoruz.
        delete normalizedSurveyJson.headerView;

        const model = new Survey.Model(normalizedSurveyJson);

        model.showTitle = false;
        model.focusFirstQuestionAutomatic = false;

        model.onComplete.add(sender => {
            onCompleteRef.current(sender);
        });

        return model;
    }, [surveyJson]);

    return (
        <Dialog
            open={open}
            onClose={() => { }}
            disableBackdropClick
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
            PaperProps={{
                className: classes.dialogPaper
            }}
            aria-labelledby="install-attention-form-title"
        >
            <DialogTitle
                id="install-attention-form-title"
                className={classes.dialogTitle}
            >
                {title}
            </DialogTitle>

            <Divider />

            <DialogContent className={classes.dialogContent}>
                {loading || !surveyModel ? (
                    <Box className={classes.loadingWrapper}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box className={classes.surveyWrapper}>
                        <Survey.Survey model={surveyModel} />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

InstallAttentionForm.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string,
    surveyJson: PropTypes.object,
    loading: PropTypes.bool,
    onComplete: PropTypes.func.isRequired
};

InstallAttentionForm.defaultProps = {
    title: 'Çamaşır Makinesi Kurulum Uyarı Onay Formu',
    surveyJson: null,
    loading: false
};

export default InstallAttentionForm;